import React, { useEffect, useState } from 'react';
import { Layout, Menu, MenuProps, Image } from 'antd';
import { Link } from 'react-router-dom';
import {
  HomeOutlined,
  AppstoreOutlined,
  EyeOutlined,
  LineChartOutlined,
  BarChartOutlined,
  EnvironmentOutlined,
  AreaChartOutlined,
  FieldTimeOutlined,
  GlobalOutlined,
  AudioOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import Logo from '../../assets/images/guarden.png';
import LogoSmall from '../../assets/images/guarden_icon.png';
import '../../css/Sidebar.css';
import { useKeycloak } from '@react-keycloak/web';
import Cookies from 'js-cookie';

const { Sider } = Layout;
const MENU_THEME = 'light';
type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  to?: string,
  children?: MenuItem[],
): MenuItem {
  return {
    key,
    icon,
    children,
    label: to ? <Link to={to}>{label}</Link> : label,
    theme: MENU_THEME,
  } as MenuItem;
}

// Define the items with parent-child relationships
const getMenuItems = (caseStudy: string | undefined) => {
  // Base species submenu (without Cyprus-specific acoustic item)
  const speciesSubmenu: MenuItem[] = [
    getItem('Observations', '3', <EyeOutlined />, '/species/observations'),
    getItem('Predictions', '4', <LineChartOutlined />, '/species/predictions'),
  ];

  // Initialize main menu items
  const baseItems: MenuItem[] = [
    getItem('Home-Introduction', '1', <HomeOutlined />, '/'), // Renamed to match requirement
    getItem('Species Distribution', '2', <AppstoreOutlined />, '', speciesSubmenu), // Renamed
    getItem('Biodiversity Indicators', '5', <BarChartOutlined />, '/indicators/conservation'), // Renamed
    getItem('Predicted Habitats', '6', <EnvironmentOutlined />, '/habitat'),
  ];

  // Clone base items to avoid mutation during modifications
  let items = [...baseItems];

  // Add Ecosystem Services for non-Barcelona cases
  if (caseStudy !== 'barcelona') {
    items.splice(1, 0, getItem('Ecosystem Services', '10', <AppstoreOutlined />, '/ecosystem')); // Renamed
  }

  // Add Acoustic Monitoring for Cyprus (as top-level item)
  if (caseStudy === 'cyprus') {
    items.splice(1, 0, getItem('Bird Acoustic Monitoring', '11', <AudioOutlined />, '/species/acoustic'));
  }

  // Add Greek Case Study submenu for Greece
  if (caseStudy === 'greece') {
    items.splice(1, 0, getItem('Acoustic Monitoring', '14', <ReadOutlined />, '', [
      getItem('Spatial Analysis', '12', <EnvironmentOutlined />, '/greekCaseStudy/spatialAnalysis'),
      getItem('Temporal Analysis', '13', <AreaChartOutlined />, '/greekCaseStudy/temporalAnalysis'),
    ]));
  }

  // Add Landscape Dynamics for Cyprus/Greece
  if (caseStudy === 'cyprus' || caseStudy === 'greece') {
    items.push(
      getItem('Landscape Dynamics', '7', <GlobalOutlined />, '', [
        getItem('Land Use/Cover Dynamics', '8', <EnvironmentOutlined />, '/landscape/land-use_land-cover'), // Renamed
        getItem('NDVI Indicator', '9', <AreaChartOutlined />, '/landscape/ndvi_indicator'),
      ])
    );
  }

  return items;
};

const Sidebar: React.FC<any> = ({ menu }) => {
  const { keycloak } = useKeycloak();

  const transformAndSetCookie = (rawCaseStudy: string): string | undefined => {
    let transformedValue: string | undefined = undefined;

    if (rawCaseStudy) {
      const lowerCaseRaw = rawCaseStudy.toLowerCase();

      switch (lowerCaseRaw) {
        case 'cyprus':
          transformedValue = 'cyprus';
          break;
        case 'greece':
          transformedValue = 'greece';
          break;
        case 'spain':
          transformedValue = 'barcelona';
          break;
        case 'france':
          transformedValue = 'pnr_narbonnaise';
          break;
        default:
          transformedValue = lowerCaseRaw;
      }
    }

    if (transformedValue) {
      Cookies.set('caseStudy', transformedValue, { expires: 7 });
    } else {
      Cookies.remove('caseStudy');
    }
    return transformedValue;
  };

  const caseStudy = transformAndSetCookie(keycloak.idTokenParsed?.case_study);
  const items = getMenuItems(caseStudy);

  // Helper function to find the parent key of a submenu (moved inside to access items)
  const findParentKey = (key: string): string | null => {
    for (const item of items) {
      if (item && 'children' in item && item.children) {
        const child = item.children.find((child: MenuItem) => child?.key === key);
        if (child) return item.key as string;
      }
    }
    return null;
  };

  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string[]>([menu]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    // Update selectedKey and manage openKeys based on the menu prop
    setSelectedKey([menu]);

    const parentKey = findParentKey(menu);
    if (parentKey) {
      setOpenKeys([parentKey]); // Open the parent key if it's a submenu
    } else {
      setOpenKeys([]); // Close all submenus if it's a top-level item
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, caseStudy]);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  return (
    <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)} theme={MENU_THEME} width={250}>
      <div
        style={{
          height: 100,
          paddingTop: 20,
          borderBottomRightRadius: 10,
          textAlign: 'center', // Center the logo
        }}
      >
        <Image
          src={collapsed ? LogoSmall : Logo} // Switch between logos based on collapsed state
          alt='logo'
          width='auto'
          height='50px'
          style={{ paddingLeft: 10, paddingRight: 10 }}
          preview={false}
        />
      </div>
      <Menu
        theme={MENU_THEME}
        selectedKeys={selectedKey}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        mode='inline'
        items={items}
        className='custom-menu'
      />
    </Sider>
  );
};

export default Sidebar;
